import { inject, Injectable } from '@angular/core';
import {
  type CreateLearningResourceLinkInput,
  type CreateLearningResourceLinkMutation,
  type CreateLearningResourceLinkMutationVariables,
  type DestroyLearningResourceLinkInput,
  type LearningResourceLink,
  type LearningResourceLinkQuery,
  type LearningResourceLinkQueryVariables,
  type MutationDestroyLearningResourceLinkArgs,
  type UpdateLearningResourceLinkInput,
  type UpdateLearningResourceLinkMutation,
  type UpdateLearningResourceLinkMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { map } from 'rxjs';
import { SharedGraphQlService } from '../../../services/shared-graphql.service';
import { assertGraphqlDataNode } from '../../../utils/type-helpers';
import { learningResourceLinkMinimal } from '../learning-resource-link.minimal';
import { learningResourceSetQuery } from './shared-learning-resource.api';

const learningResourceLinkQuery = gql`
  query LearningResourceLink($learningResourceLinkId: ID!) {
    node(id: $learningResourceLinkId) {
      ... on LearningResourceLink {
        ...LearningResourceLinkMinimal
      }
    }
  }
  ${learningResourceLinkMinimal}
`;

@Injectable({
  providedIn: 'root',
})
export class SharedLearningResourceLinkApi {
  private readonly sharedGraphQlService = inject(SharedGraphQlService);

  watchLearningResourceLink(learningResourceLinkId: string) {
    return this.sharedGraphQlService
      .watchQuery<LearningResourceLinkQuery, LearningResourceLinkQueryVariables>({
        query: learningResourceLinkQuery,
        variables: {
          learningResourceLinkId: learningResourceLinkId,
        },
      })
      .pipe(map((result) => assertGraphqlDataNode(result, 'LearningResourceLink')));
  }

  createLearningResourceLink(input: CreateLearningResourceLinkInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<CreateLearningResourceLinkMutation, CreateLearningResourceLinkMutationVariables>({
          mutation: gql`
            mutation CreateLearningResourceLink($input: CreateLearningResourceLinkInput!) {
              createLearningResourceLink(input: $input) {
                errors {
                  message
                  path
                }
                learningResourceLink {
                  ...LearningResourceLinkMinimal
                }
              }
            }
            ${learningResourceLinkMinimal}
          `,
          variables: { input },
          refetchQueries: [
            {
              query: learningResourceSetQuery,
              variables: { learningResourceSetId: input.learningResourceSetId },
            },
          ],
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createLearningResourceLink.learningResourceLink))
    );
  }

  updateLearningResourceLink(input: UpdateLearningResourceLinkInput) {
    return (
      this.sharedGraphQlService
        .mutateV2<UpdateLearningResourceLinkMutation, UpdateLearningResourceLinkMutationVariables>({
          mutation: gql`
            mutation UpdateLearningResourceLink($input: UpdateLearningResourceLinkInput!) {
              updateLearningResourceLink(input: $input) {
                errors {
                  message
                  path
                }
                learningResourceLink {
                  ...LearningResourceLinkMinimal
                }
              }
            }
            ${learningResourceLinkMinimal}
          `,
          variables: { input },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateLearningResourceLink.learningResourceLink))
    );
  }

  destroyLearningResourceLink(input: DestroyLearningResourceLinkInput, learningResourceSetId: string) {
    return this.sharedGraphQlService.mutateV2<
      { destroyLearningResourceLink: LearningResourceLink },
      MutationDestroyLearningResourceLinkArgs
    >({
      mutation: gql`
        mutation DestroyLearningResourceLink($input: DestroyLearningResourceLinkInput!) {
          destroyLearningResourceLink(input: $input) {
            errors {
              message
              path
            }
          }
        }
      `,
      variables: {
        input,
      },
      refetchQueries: [
        {
          query: learningResourceSetQuery,
          variables: { learningResourceSetId },
        },
      ],
    });
  }
}
